import { useMemo, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { Plus } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { graphql } from '@/gql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Select } from '@/components/ui/Select';
import { formatCurrency, formatInteger } from '@/lib/format';
import { downloadAuthenticatedCsv } from '@/lib/downloadCsv';
import { useListHref } from '@/hooks/useListLocation';
import { isDefault, useDebouncedUrlField, useUrlState } from '@/hooks/useUrlState';
import { DISCOUNTS_ADVANCED_KEYS, discountsUrlSchema, type DiscountTypeFilter } from './discountsUrlState';

const DiscountsDoc = graphql(/* GraphQL */ `
  query Discounts(
    $filter: DiscountFilter
    $includeInactive: Boolean
    $page: Int
    $pageSize: Int
  ) {
    discounts(
      filter: $filter
      includeInactive: $includeInactive
      page: $page
      pageSize: $pageSize
    ) {
      nodes {
        id
        name
        type
        isActive
        totalDiscount30d
        usageCount30d
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const DiscountTypesForFilterDoc = graphql(/* GraphQL */ `
  query DiscountTypesForFilter {
    discountTypes {
      type
      label
    }
  }
`);

const SetDiscountsActiveDoc = graphql(/* GraphQL */ `
  mutation SetDiscountsActive($ids: [ID!]!, $isActive: Boolean!) {
    setDiscountsActive(ids: $ids, isActive: $isActive) {
      succeeded
      failed {
        id
        reason
      }
    }
  }
`);

const ExportDiscountsCsvDoc = graphql(/* GraphQL */ `
  query ExportDiscountsCsv($filter: DiscountFilter) {
    exportDiscountsCsv(filter: $filter) {
      url
      expiresAt
    }
  }
`);

interface DiscountRow {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  totalDiscount30d: number;
  usageCount30d: number;
}

type DiscountAction = 'SET_ACTIVE' | 'SET_INACTIVE';

export function DiscountsListPage() {
  const apollo = useApolloClient();
  const navigate = useNavigate();
  const { state: url, patch, setFilters } = useUrlState(discountsUrlSchema);
  const listHref = useListHref();
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));
  // The Filters panel opens on mount when a filter inside it is set (a reload, Back, an inbound link).
  const [filtersOpen] = useState(() => DISCOUNTS_ADVANCED_KEYS.some((key) => !isDefault(discountsUrlSchema, url, key)));
  const [selection, setSelection] = useState<Record<string, boolean>>({});

  const filter = useMemo(
    () => ({
      search: url.search || null,
      type: url.type ? [url.type] : null,
    }),
    [url.search, url.type],
  );

  const { data, previousData, loading, refetch } = useQuery(DiscountsDoc, {
    variables: { filter, includeInactive: true, page: url.page, pageSize: url.size },
  });
  const discountsData = data ?? previousData;
  const { data: typesData } = useQuery(DiscountTypesForFilterDoc);
  const [setDiscountsActive] = useMutation(SetDiscountsActiveDoc);

  const rows: DiscountRow[] = useMemo(
    () =>
      (discountsData?.discounts.nodes ?? []).map((n) => ({
        id: n.id,
        name: n.name,
        type: n.type,
        isActive: n.isActive,
        totalDiscount30d: n.totalDiscount30d,
        usageCount30d: n.usageCount30d,
      })),
    [discountsData],
  );
  const pageInfo = discountsData?.discounts.pageInfo;

  const typeLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of typesData?.discountTypes ?? []) map.set(t.type, t.label);
    return map;
  }, [typesData]);

  const columns = useMemo<ColumnDef<DiscountRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Link
            to={`/discounts/${row.original.id}`}
            state={{ from: listHref }}
            className="font-medium text-gray-900 hover:text-brand dark:text-white"
          >
            {row.original.name}
          </Link>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Badge tone="brand">
            {typeLabels.get(row.original.type) ?? row.original.type}
          </Badge>
        ),
      },
      {
        id: 'usageCount30d',
        header: 'Usage (30d)',
        cell: ({ row }) => (
          <Link
            to={`/orders?discounts=${encodeURIComponent(row.original.id)}`}
            className="tabular-nums text-brand hover:underline"
            title="View orders filtered by this discount"
          >
            {formatInteger(row.original.usageCount30d)}
          </Link>
        ),
      },
      {
        id: 'totalDiscount30d',
        header: 'Total (30d)',
        cell: ({ row }) => formatCurrency(row.original.totalDiscount30d),
      },
      {
        id: 'isActive',
        accessorKey: 'isActive',
        header: 'Active',
        cell: ({ row }) => (
          <Badge tone={row.original.isActive ? 'success' : 'neutral'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [typeLabels, listHref],
  );

  const handleExport = async () => {
    const { data: exportData } = await apollo.query({
      query: ExportDiscountsCsvDoc,
      variables: { filter },
      fetchPolicy: 'network-only',
    });
    if (exportData?.exportDiscountsCsv?.url) {
      await downloadAuthenticatedCsv(exportData.exportDiscountsCsv.url);
    }
  };

  const runBulkActive = async (ids: string[], active: boolean) => {
    await setDiscountsActive({ variables: { ids, isActive: active } });
    setSelection({});
    await refetch();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Discounts
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Promotions and staff discounts. The 30-day stats reflect real
            usage in the current scope.
          </p>
        </div>
        <Button
          onClick={() => navigate('/discounts/new', { state: { from: listHref } })}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          New discount
        </Button>
      </div>

      <FilterBar
        defaultOpen={filtersOpen}
        search={{
          id: 'admin.discounts.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Search discounts…',
        }}
        advanced={
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700 dark:text-gray-200">
              Type
            </span>
            <Select
              id="admin.discounts.type"
              value={url.type}
              onChange={(event) => setFilters({ type: event.target.value as DiscountTypeFilter })}
              options={[
                { value: '', label: 'Any type' },
                ...(typesData?.discountTypes.map((t) => ({
                  value: t.type,
                  label: t.label,
                })) ?? []),
              ]}
            />
          </label>
        }
      />

      <DataGrid<DiscountRow, DiscountAction>
        data={rows}
        columns={columns}
        loading={loading && !discountsData}
        rowId={(row) => row.id}
        onExport={handleExport}
        selection={{ state: selection, onChange: setSelection }}
        actions={[
          {
            id: 'SET_ACTIVE',
            label: 'Activate',
            variant: 'outline',
            onRun: (selected) =>
              runBulkActive(
                selected.map((r) => r.id),
                true,
              ),
          },
          {
            id: 'SET_INACTIVE',
            label: 'Deactivate',
            variant: 'outline',
            onRun: (selected) =>
              runBulkActive(
                selected.map((r) => r.id),
                false,
              ),
          },
        ]}
        allowedActionsForRow={(row) =>
          row.isActive ? ['SET_INACTIVE'] : ['SET_ACTIVE']
        }
        rowMuted={(row) => !row.isActive}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
